import React, {Component} from 'react';

import {
	TEST_STEPS,
	TEST_STEPS_BY_ID,
	YOUTH_TEST_STEPS_BY_ID,
	CHILD_TEST_STEPS_BY_ID,
	CHILD_SCRNG_TEST_STEPS_BY_ID,
	CHILD_SCRNG_EXPECTED_VALUES,
	CHILD_FOLLOWUP_EXPECTED_VALUES,
	Z_SCORE_OFFSET,
	Z_SCORE_MAX,
} from 'constants/index';
import strings from 'utils/strings';
import roundWithDecimals from 'utils/roundWithDecimals';

class Subtest extends Component {
	constructor(props) {
		super(props);
		this.state = {};
	}

	// eslint-disable-next-line complexity
	render() {
		const {subtest, type, testResult, testType} = this.props;

		// Use testType when provided (you said you now pass it), otherwise fall back to old "type"
		const resolvedType = testType || type;

		const isChildScreening = resolvedType === 'child-screening';
		const isYouthFollowup = resolvedType === 'youth-followup';
		const isChildFollowup = resolvedType === 'child-followup';
		const isFollowup = isYouthFollowup || isChildFollowup;

		// Child-screening AND child-followup texts can contain \n\n and should render line breaks
		const multilineStyle =
			isChildScreening || isChildFollowup
				? {whiteSpace: 'pre-line'}
				: undefined;

		const {testUser} = testResult;

		const followupGrade =
			testUser.education?.youthGrade ??
			testUser.education?.grade ??
			testUser.education?.garde ??
			testUser.class ??
			testUser.ageGroup;

		// normalize to number when possible (important for object keys like 7,8,9)
		const followupGradeKey =
			followupGrade === 'adult'
				? 'adult'
				: Number.isFinite(Number(followupGrade))
					? Number(followupGrade)
					: followupGrade;

		const foreign =
			resolvedType.includes('screening') &&
			testUser.homeLanguage &&
			testUser.homeLanguage.grewUpInBilingualHome === true
				? 1
				: !resolvedType.includes('screening') &&
					  testUser.languagesInHome &&
					  testUser.languagesInHome !== 'one'
					? 1
					: 0;

		const gender = testUser.gender === 'f' ? 0 : 1;

		const testUserPronoun =
			testUser.gender === 'm'
				? 'Han'
				: testUser.gender === 'f'
					? 'Hon'
					: 'Hen';

		// Grade/age group selection
		// --- Grade/age group selection (FIXED) ---
		const parseMaybeGrade = (v) => {
			if (v === null || v === undefined) return null;
			if (typeof v === 'number') return v;
			// handles "7", "7.klass", "klass 7", etc.
			const m = String(v).match(/\d+/);
			return m ? Number(m[0]) : null;
		};

		let classInfo;

		if (resolvedType === 'child') {
			// Child test uses class/ageGroup
			classInfo = testUser.class ?? testUser.ageGroup;
		} else if (
			resolvedType === 'child-screening' ||
			resolvedType === 'youth-screening'
		) {
			// Screening uses education.youthGrade
			classInfo = testUser.education?.youthGrade;
		} else if (isFollowup) {
			// Followup: grade is often stored on testResult.info.class (or sometimes testUser.class)
			classInfo =
				testResult.info?.class ??
				testUser.class ??
				testUser.education?.youthGrade;
		} else {
			// Adult-ish fallback
			classInfo =
				testUser.education?.garde ||
				testUser.education?.grade ||
				testUser.class ||
				strings.common.theagegroup;
		}

		// Normalize to a numeric grade when possible (so norms[7]/norms[8]/norms[9] work)
		const numericGrade = parseMaybeGrade(classInfo);

		const ageGroup =
			classInfo === 'yrkesfag' ||
			classInfo === 'bachelor' ||
			classInfo === 'master' ||
			classInfo === 'phd' ||
			classInfo === 'other' ||
			classInfo === 'studiespesialisering'
				? 'adult'
				: (numericGrade ?? classInfo);

		const isTestOwner = testResult.isTestOwner;

		let onlyFirstName = strings.common.hiddenname;
		if (testUser.firstName) onlyFirstName = testUser.firstName;

		const {totalCorrect, id, zScore, vars} = subtest;

		// Safe helpers (prevent NaN crashes)
		const safeRound = (value, decimals = 2) => {
			const n = Number(value);
			return Number.isFinite(n) ? roundWithDecimals(n, decimals) : '';
		};

		// For child-followup we do NOT want to show a z-score number in the variable text templates
		const zScoreText =
			!isChildFollowup && Number.isFinite(zScore)
				? safeRound(zScore, 2)
				: '';

		const zScoreImageLabelWidth = 20.79;
		const zScoreImageRightPaddingWidth = 2.25;
		const zScoreChartWidth =
			100 - zScoreImageRightPaddingWidth - zScoreImageLabelWidth;

		// --------------------------
		// Resolve subtestInfo by type
		// --------------------------
		let subtestInfo = null;
		switch (resolvedType) {
			case 'child':
			case 'youth-followup':
			case 'child-followup':
				// Follow-up (both kinds) currently uses CHILD_TEST_STEPS_BY_ID in your app
				subtestInfo = CHILD_TEST_STEPS_BY_ID[id];
				break;

			case 'adult-screening':
				subtestInfo = TEST_STEPS_BY_ID[id];
				break;

			case 'youth-screening':
				subtestInfo = YOUTH_TEST_STEPS_BY_ID[id];
				break;

			case 'child-screening':
				subtestInfo = CHILD_SCRNG_TEST_STEPS_BY_ID[id];
				break;

			default:
				subtestInfo = null;
		}

		// Safety guard (prevents crashes if mapping is missing)
		if (!subtestInfo) return null;

		// --------------------------
		// Child-followup text overrides
		// --------------------------
		const effectiveReportHeading =
			isChildFollowup && subtestInfo.childFollowupReportHeading
				? subtestInfo.childFollowupReportHeading
				: subtestInfo.reportHeading;

		const effectiveReportExplaination =
			isChildFollowup && subtestInfo.childFollowupReportExplaination
				? subtestInfo.childFollowupReportExplaination
				: subtestInfo.reportExplaination;

		const effectiveReportBHexplaination =
			isChildFollowup && subtestInfo.childFollowupReportBHexplaination
				? subtestInfo.childFollowupReportBHexplaination
				: subtestInfo.reportBHexplaination;

		const effectiveReportVariableText =
			isChildFollowup && subtestInfo.childFollowupReportVariableText
				? subtestInfo.childFollowupReportVariableText
				: subtestInfo.reportVariableText;

		const effectiveReportVariableTextBH =
			isChildFollowup && subtestInfo.childFollowupReportVariableTextBH
				? subtestInfo.childFollowupReportVariableTextBH
				: subtestInfo.reportVariableTextBH;

		const subtestQuestionCount = subtestInfo.questionCount;
		const subtestLabel = subtestInfo.label;
		const subTestReportHeading = effectiveReportHeading;

		let totalCorrectLabel;
		let formattedVariableText;
		let subtestExplanation = '';
		let meanScore;
		let standardDeviation;

		// Helper: safe getPredictedScore (never crashes UI)
		const gradeValueForPrediction = isFollowup
			? (testUser.education?.youthGrade ??
				testUser.education?.grade ??
				testUser.education?.garde ??
				testUser.class ??
				testUser.ageGroup)
			: testUser.ageGroup;

		const safeGetPredictedScore = () => {
			if (typeof subtestInfo.getPredictedScore !== 'function')
				return strings.common.notavailable;
			try {
				// child/followup getPredictedScore typically expects (foreignValue, gradeValue)
				return subtestInfo.getPredictedScore(
					foreign,
					gradeValueForPrediction
				);
			} catch (e) {
				return strings.common.notavailable;
			}
		};

		// --------------------------
		// Child-screening (special V2 strings)
		// --------------------------
		if (isChildScreening) {
			const displayName = isTestOwner
				? onlyFirstName
				: strings.common.hiddenname;
			const scoreText = `${Math.round(Number(totalCorrect) || 0)}`;

			formattedVariableText = strings.formatString(
				effectiveReportVariableText,
				displayName,
				scoreText
			);

			// V2 child-screening explanations are plain text (no placeholders)
			const childScreeningGrade = Number(testUser.education?.youthGrade);
			const expectedKey = subtestInfo.expectedKey;

			const expectedScore =
				CHILD_SCRNG_EXPECTED_VALUES[expectedKey]?.[
					childScreeningGrade
				] ?? strings.common.notavailable;

			subtestExplanation = strings.formatString(
				effectiveReportExplaination,
				<b>{expectedScore}</b>
			);
		}
		// --------------------------
		// Child test + followups share the "child" formatting logic
		// --------------------------
		else if (
			resolvedType === 'child' ||
			isYouthFollowup ||
			isChildFollowup
		) {
			let myVars;
			if (vars) myVars = JSON.parse(vars);

			// IMPORTANT: child-followup should NOT show predicted mean/SD (and must not call getPredictedScore)
			const shouldShowNormNumbers = !isChildFollowup;
			const childFollowupExpectedKey = subtestInfo.expectedKey;
			const childFollowupGrade = Number(ageGroup);

			const expectedValue =
				CHILD_FOLLOWUP_EXPECTED_VALUES[childFollowupExpectedKey]?.[
					childFollowupGrade
				];

			const childFollowupExpectedScore =
				isChildFollowup &&
				childFollowupExpectedKey &&
				expectedValue !== undefined
					? expectedValue
					: strings.common.notavailable;

			const formatChildFollowupExplanation = () => {
				return strings.formatString(
					effectiveReportExplaination,
					<b>{childFollowupExpectedScore}</b>
				);
			};

			switch (id) {
				case 0: {
					totalCorrectLabel = `${totalCorrect}  rette`;

					formattedVariableText = strings.formatString(
						effectiveReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
						`${totalCorrect}`,
						zScoreText,
						`${testUserPronoun}`
					);

					if (!shouldShowNormNumbers) {
						subtestExplanation = formatChildFollowupExplanation();
						break;
					}

					meanScore = safeGetPredictedScore();
					standardDeviation = subtestInfo.norms?.[ageGroup]
						?.standardDeviation
						? subtestInfo.norms[ageGroup].standardDeviation
						: strings.common.notavailable;

					subtestExplanation = strings.formatString(
						effectiveReportExplaination,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					break;
				}

				case 3: {
					let totalErrors = 0;
					let duration = 0;

					formattedVariableText = strings.formatString(
						effectiveReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
						`${safeRound(totalCorrect, 2)}`,
						zScoreText,
						`${testUserPronoun}`
					);

					if (myVars?.duration) {
						duration = myVars.duration;
						totalErrors = myVars.totalErrors;
					}
					totalCorrectLabel = `\n${strings.common.timeused}: ${Math.round(
						duration / 1000
					)} sekunder\nAntal fel: ${totalErrors}`;

					if (!shouldShowNormNumbers) {
						subtestExplanation = formatChildFollowupExplanation();
						break;
					}

					meanScore = safeGetPredictedScore();
					standardDeviation = subtestInfo.norms?.[ageGroup]
						?.standardDeviation
						? subtestInfo.norms[ageGroup].standardDeviation
						: strings.common.notavailable;

					subtestExplanation = strings.formatString(
						ageGroup == 1 || ageGroup == 0
							? effectiveReportBHexplaination
							: effectiveReportExplaination,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);

					break;
				}

				case 4: {
					totalCorrectLabel = `${totalCorrect}  ${strings.testView.wordreadcorrectly}`;

					formattedVariableText = strings.formatString(
						effectiveReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
						`${totalCorrect}`,
						zScoreText,
						`${testUserPronoun}`
					);

					if (!shouldShowNormNumbers) {
						subtestExplanation = formatChildFollowupExplanation();
						break;
					}

					meanScore = safeGetPredictedScore();
					standardDeviation =
						subtestInfo.norms?.[ageGroup]?.standardDeviation ??
						strings.common.notavailable;

					subtestExplanation = strings.formatString(
						effectiveReportExplaination,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
					break;
				}

				case 6: {
					totalCorrectLabel = `${totalCorrect}  ${subtestQuestionCount}`;

					if (!shouldShowNormNumbers) {
						// child-followup: keep text but avoid any predicted-score work
						subtestExplanation = effectiveReportExplaination;
					} else {
						meanScore = safeGetPredictedScore();
						standardDeviation =
							subtestInfo.norms?.[ageGroup]?.standardDeviation ??
							strings.common.notavailable;

						subtestExplanation = strings.formatString(
							effectiveReportExplaination,
							<b>{meanScore}</b>,
							<b>{standardDeviation}</b>
						);
					}

					formattedVariableText =
						ageGroup == 0 && effectiveReportVariableTextBH
							? strings.formatString(
									effectiveReportVariableTextBH,
									`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
									`${totalCorrect}`,
									`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
									`${testUserPronoun}`
								)
							: strings.formatString(
									effectiveReportVariableText,
									`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
									`${totalCorrect}`,
									zScoreText,
									`${testUserPronoun}`
								);

					break;
				}

				case 8: {
					// Child-followup only: Phoneme deletion (no norms/predicted required)
					totalCorrectLabel = `${totalCorrect} av ${subtestQuestionCount}`;

					formattedVariableText = strings.formatString(
						effectiveReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
						`${totalCorrect}`,
						zScoreText,
						`${testUserPronoun}`
					);

					subtestExplanation = formatChildFollowupExplanation();
					break;
				}

				default: {
					totalCorrectLabel = `${totalCorrect} av ${subtestQuestionCount}`;

					formattedVariableText = strings.formatString(
						effectiveReportVariableText,
						`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
						`${safeRound(totalCorrect, 2)}`,
						zScoreText,
						`${testUserPronoun}`
					);

					if (!shouldShowNormNumbers) {
						subtestExplanation = effectiveReportExplaination;
						break;
					}

					meanScore = safeGetPredictedScore();
					standardDeviation = subtestInfo.norms?.[ageGroup]
						?.standardDeviation
						? subtestInfo.norms[ageGroup].standardDeviation
						: strings.common.notavailable;

					subtestExplanation = strings.formatString(
						effectiveReportExplaination,
						<b>{meanScore}</b>,
						<b>{standardDeviation}</b>
					);
				}
			}
		}
		// --------------------------
		// Adult-screening / youth-screening (unchanged behavior)
		// --------------------------
		else {
			if (subtestInfo.name === TEST_STEPS.SKRIVEHASTIGHET) {
				totalCorrectLabel = `${totalCorrect} falza ${strings.common.wordsperminute}`;

				meanScore = subtestInfo.getPredictedScore(
					gender,
					foreign,
					testUser.education?.youthGrade
				);

				if (
					meanScore === strings.common.notavailable &&
					resolvedType === 'adult-screening'
				) {
					meanScore = subtestInfo.norms?.default?.average;
				}

				standardDeviation = subtestInfo.norms?.[ageGroup]
					? subtestInfo.norms[ageGroup].standardDeviation
					: strings.common.notavailable;

				if (
					standardDeviation === strings.common.notavailable &&
					resolvedType === 'adult-screening'
				) {
					standardDeviation =
						subtestInfo.norms?.default?.standardDeviation;
				}

				subtestExplanation = strings.formatString(
					subtestInfo.reportExplaination,
					<b>{meanScore}</b>,
					<b>{standardDeviation}</b>
				);

				formattedVariableText = strings.formatString(
					subtestInfo.reportVariableText,
					`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
					`${totalCorrect}`,
					`${safeRound(zScore, 2)}`,
					`${testUserPronoun}`
				);
			} else {
				totalCorrectLabel = `${totalCorrect} av ${subtestQuestionCount}`;

				meanScore = subtestInfo.getPredictedScore(
					gender,
					foreign,
					testUser.education?.youthGrade
				);

				if (
					meanScore === strings.common.notavailable &&
					resolvedType === 'adult-screening'
				) {
					meanScore = subtestInfo.norms?.default?.average;
				}

				standardDeviation = subtestInfo.norms?.[ageGroup]
					? subtestInfo.norms[ageGroup].standardDeviation
					: strings.common.notavailable;

				if (
					standardDeviation === strings.common.notavailable &&
					resolvedType === 'adult-screening'
				) {
					standardDeviation =
						subtestInfo.norms?.default?.standardDeviation;
				}

				subtestExplanation = strings.formatString(
					subtestInfo.reportExplaination,
					<b>{meanScore}</b>,
					<b>{standardDeviation}</b>
				);

				formattedVariableText = strings.formatString(
					subtestInfo.reportVariableText,
					`${isTestOwner ? onlyFirstName : strings.common.hiddenname}`,
					`${totalCorrect}`,
					`${safeRound(zScore, 2)}`,
					`${testUserPronoun}`
				);
			}
		}

		const testZScorePercentage = (zScore + Z_SCORE_OFFSET) / Z_SCORE_MAX;
		const testZChartBarLeft =
			zScoreImageLabelWidth + testZScorePercentage * zScoreChartWidth;

		return (
			<div className="sub-test-Report" key={id}>
				<br />
				<div style={{paddingLeft: '10px'}}>
					<h3
						className="sub-test-Report__name"
						style={{color: '#0f4256', marginLeft: '40px'}}
					>
						{`${this.props.order}) ${subtestLabel}`}
					</h3>

					<div className="sub-test-report__scores">
						<div className="sub-test-Report">
							<p
								className="sub-test-Report__heading"
								style={multilineStyle}
							>
								{subTestReportHeading}
							</p>

							<p style={multilineStyle}>{subtestExplanation}</p>

							<p>
								{formattedVariableText}
								<span>
									{zScore <= -1 && (
										<span className="lit-indented">
											<span className="lit-indented__under">
												{' under '}
											</span>
											{strings.testView.averageForGrade}
										</span>
									)}
									{zScore >= 1 && (
										<span className="lit-indented">
											<span className="lit-indented__over">
												{' ' + strings.common.over + ' '}
											</span>
											{strings.testView.averageForGrade}
										</span>
									)}
									{zScore < 1 &&
										zScore > -1 &&
										ageGroup != 0 && (
											<span className="lit-indented">
												<span className="lit-indented__in">
													{' ' + strings.testView.inom + ' '}
												</span>
												{strings.testView.averageForGrade}
											</span>
										)}
									{zScore < 1 &&
										zScore > -1 &&
										ageGroup == 0 &&
										id == 6 && <span>{''}</span>}
								</span>
							</p>

							<div className="sub-test-Report__margin"></div>
						</div>

						{this.props.children}
					</div>
				</div>

				{[2, 4, 6, '2', '4'].includes(this.props.order) && (
					<span
						className="mynewpage"
						style={{pageBreakAfter: 'always', display: 'block'}}
					></span>
				)}
			</div>
		);
	}
}

export default Subtest;
